import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { EmptyState, ErrorState, LoadingShell } from './state';

function defaultEmpty<TData>(data: TData): boolean {
  return Array.isArray(data) ? data.length === 0 : data == null;
}

/**
 * Consumes a react-query result directly and renders the standard
 * loading → error → empty → data state machine, so route components
 * don't hand-roll `isPending`/`isError`/empty checks around every query.
 *
 * Gates on `isPending` (v5) rather than `isLoading` — this is the query's
 * `data === undefined` state, true both while a query is fetching for the
 * first time AND while it's disabled (`enabled: false`) and has never run.
 * **Callers with a permission-gated query (`enabled: canSell` etc.) must
 * keep their own outer gate around `<QueryBoundary>`** — otherwise a
 * disabled query renders the loading skeleton forever instead of nothing.
 *
 * `empty` decides whether the resolved data counts as "no results" and
 * defaults to `Array.isArray(data) ? data.length === 0 : data == null` —
 * override for shapes where that default is wrong (e.g. an object with an
 * empty nested array). Loading/error rendering can be overridden per call
 * site via `loading`/`errorTitle`/`emptyState` — defaults cover the common
 * case.
 */
export function QueryBoundary<TData>({
  query,
  children,
  empty,
  emptyState,
  loading,
  loadingRows,
  errorTitle,
}: {
  query: UseQueryResult<TData>;
  children: (data: TData) => ReactNode;
  empty?: (data: TData) => boolean;
  emptyState?: ReactNode;
  loading?: ReactNode;
  loadingRows?: number;
  errorTitle?: string;
}) {
  if (query.isPending) {
    return <>{loading ?? <LoadingShell rows={loadingRows} />}</>;
  }

  if (query.isError) {
    return (
      <ErrorState
        title={errorTitle}
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }

  // Narrowed: neither isPending nor isError, so react-query's discriminated
  // UseQueryResult union guarantees isSuccess and `data: TData` here — no
  // cast needed.
  const data = query.data;

  if ((empty ?? defaultEmpty)(data)) {
    return <>{emptyState ?? <EmptyState title="Nothing here yet" />}</>;
  }

  return <>{children(data)}</>;
}
